import {
  Box,
  Typography,
  TextField,
  type TextFieldProps,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useState, useEffect, useRef } from 'react';
import { debounce } from '@mui/material/utils';
import { osirisTokens } from '@/theme/tokens';

/** Figma number-input stepper: two stacked 20×12 cells at the field's right */
function NumberStepper({ onStep }: { onStep: (direction: 1 | -1) => void }) {
  const cellSx = {
    all: 'unset' as const,
    boxSizing: 'border-box' as const,
    cursor: 'pointer',
    display: 'flex' as const,
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 12,
    bgcolor: osirisTokens.bg.header,
    color: osirisTokens.icons.secondary,
    '&:hover': { bgcolor: osirisTokens.bg.secondary },
    '&:active': { bgcolor: osirisTokens.bg.active, color: '#fff' },
  };
  return (
    <InputAdornment position="end">
      <Box
        className="number-stepper"
        sx={{ display: 'flex', flexDirection: 'column', gap: '1px', mr: -0.5 }}
      >
        <Box
          component="button"
          type="button"
          tabIndex={-1}
          className="number-stepper-up"
          onClick={() => onStep(1)}
          sx={cellSx}
        >
          <ArrowDropUpIcon sx={{ fontSize: 14 }} />
        </Box>
        <Box
          component="button"
          type="button"
          tabIndex={-1}
          className="number-stepper-down"
          onClick={() => onStep(-1)}
          sx={cellSx}
        >
          <ArrowDropDownIcon sx={{ fontSize: 14 }} />
        </Box>
      </Box>
    </InputAdornment>
  );
}

interface InputStandardProps extends Omit<TextFieldProps, 'variant' | 'fullWidth'> {
  label?: string;
  helperText?: string;
  error?: boolean;
  isDebounce?: boolean;
  debounceDelay?: number;
  isRequired?: boolean;
  /** Marks a non-required field with a muted "(Optional)" suffix (designer convention). */
  isOptional?: boolean;
  /** Muted bracketed note after the label, e.g. "(locked while installed on a drone)". */
  labelNote?: string;
  numberMin?: number;
  numberMax?: number;
  ref?: React.Ref<HTMLDivElement>;
}

export function InputTextField({
  label,
  helperText,
  error,
  isDebounce = false,
  debounceDelay = 400,
  isRequired = false,
  isOptional = false,
  labelNote,
  numberMin,
  numberMax,
  sx,
  value,
  onChange,
  onKeyDown,
  ref,
  slotProps: externalSlotProps,
  ...props
}: InputStandardProps) {
  const [internalValue, setInternalValue] = useState<string>((value as string) || '');
  const [isDebouncing, setIsDebouncing] = useState(false);
  const onChangeRef = useRef(onChange);
  const inputElRef = useRef<HTMLInputElement | null>(null);

  // Keep onChange ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync internal value with external value prop (guarded adjust during render —
  // effects must not setState synchronously).
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    if (value !== undefined && value !== null) {
      setInternalValue(value as string);
    }
  }

  // Debounce logic (isDebouncing is raised in handleChange, where typing happens)
  useEffect(() => {
    if (!isDebounce || !onChangeRef.current) {
      return;
    }

    const delayDebounceFn = debounce((val: string) => {
      const syntheticEvent = {
        target: { value: val },
      } as React.ChangeEvent<HTMLInputElement>;
      onChangeRef.current?.(syntheticEvent);
      setIsDebouncing(false);
    }, debounceDelay);

    delayDebounceFn(internalValue);

    return () => {
      delayDebounceFn.clear();
    };
  }, [internalValue, isDebounce, debounceDelay]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (props.type === 'number' && newValue !== '') {
      const num = Number(newValue);
      if (numberMax !== undefined && num > numberMax) return;
      if (numberMin !== undefined && num < numberMin) return;
    }

    setInternalValue(newValue);

    if (isDebounce) {
      setIsDebouncing(true);
    } else if (onChange) {
      // If not debouncing, call onChange immediately
      onChange(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (props.type === 'number' && numberMin !== undefined && numberMin >= 0) {
      if (e.key === '-' || e.key === 'e') e.preventDefault();
    }
    onKeyDown?.(e);
  };

  const handleStep = (direction: 1 | -1) => {
    const input = inputElRef.current;
    if (!input) return;
    const htmlInput = (externalSlotProps as Record<string, unknown>)?.htmlInput as
      Record<string, unknown> | undefined;
    const step = Number(htmlInput?.step) || 1;
    const min = numberMin ?? (htmlInput?.min !== undefined ? Number(htmlInput.min) : undefined);
    const max = numberMax ?? (htmlInput?.max !== undefined ? Number(htmlInput.max) : undefined);
    const current = Number(input.value);
    let next = (Number.isNaN(current) ? 0 : current) + direction * step;
    if (min !== undefined && next < min) next = min;
    if (max !== undefined && next > max) next = max;
    // Write through the native setter and dispatch a real 'input' event so
    // React fires onChange with the actual field as target. A hand-built
    // {target:{value}} object never updates uncontrolled inputs
    // (react-hook-form register) — the DOM value stayed unchanged.
    const setValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    setValue?.call(input, String(next));
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  return (
    <Box>
      {label && (
        // Figma control labels (28398:3897): txt-muted 13px — the input text
        // itself is the brightest element, labels stay dim (same as SelectStandard)
        <Typography
          variant="body2"
          sx={{ mb: '5px', fontSize: 13, color: osirisTokens.text.muted }}
        >
          {label}
          {isRequired && (
            // txt-active asterisk per the drawer mock (Figma 28711:30875)
            <Typography component="span" sx={{ color: osirisTokens.text.active, ml: 0.5 }}>
              *
            </Typography>
          )}
          {isOptional && (
            <Typography component="span" sx={{ color: 'text.disabled', ml: 0.5, fontSize: '13px' }}>
              (Optional)
            </Typography>
          )}
          {labelNote && (
            <Typography component="span" sx={{ color: 'text.disabled', ml: 0.5, fontSize: '13px' }}>
              ({labelNote})
            </Typography>
          )}
        </Typography>
      )}
      <TextField
        ref={ref}
        inputRef={inputElRef}
        fullWidth
        variant="outlined"
        error={error}
        helperText={helperText ?? (label ? ' ' : undefined)}
        value={isDebounce ? internalValue : value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
          },
          '& .MuiFormHelperText-root': {
            mx: 0,
            mt: '2px',
          },
          ...sx,
        }}
        slotProps={{
          ...externalSlotProps,
          // merge (not replace) the input slot so callers can add adornments
          // without dropping the debounce spinner / number stepper
          input: {
            endAdornment:
              isDebounce && isDebouncing && internalValue ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : props.type === 'number' && !props.disabled ? (
                <NumberStepper onStep={handleStep} />
              ) : undefined,
            ...((externalSlotProps as Record<string, unknown>)?.input as Record<string, unknown>),
          },
          ...(props.type === 'number' && (numberMin !== undefined || numberMax !== undefined)
            ? {
                htmlInput: {
                  ...(numberMin !== undefined && { min: numberMin }),
                  ...(numberMax !== undefined && { max: numberMax }),
                  ...((externalSlotProps as Record<string, unknown>)?.htmlInput as Record<
                    string,
                    unknown
                  >),
                },
              }
            : {}),
        }}
        {...props}
      />
    </Box>
  );
}

InputTextField.displayName = 'InputTextField';
